import React from 'react'
import ReactDOM from 'react-dom'
import { Flags } from 'react-feature-flags'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import SphericalMercator from '@mapbox/sphericalmercator'
import {
    setBasemap as setBasemapConnect,
    setOpacity as setOpacityConnect,
    toggleLayer as toggleLayerConnect
} from '../actions/layers'
import Slider from './Slider'
import { basemaps, contextualLayers as contextualLayersConfig } from '../config'
import DataBasinLogo from '../../img/logo-databasin.png'
import ExternalLogo from '../../img/external.png'
import NewIcon from '../../img/icon-new.svg'
import Checkbox from './Checkbox'
import SVGWrapper from './SVGWrapper'
import InfoIcon from '../../img/icon-information.svg'
import Accordion from './Accordion'
import { setDialog as setDialogConnect } from '../actions/navigation'

export class LayersComponent extends React.Component {
    constructor(props) {
        super(props)

        this.categories = [...new Set(this.props.contextualLayers.map(layer => layer.category))]

        this.state = {
            activePopup: ''
        }

        this.refMap = this.props.contextualLayers.reduce((output, layer) => {
            return { ...output, ...{ [layer.name]: React.createRef() } }
        }, {})

        this.popupRef = React.createRef()
    }

    componentDidMount() {
        window.addEventListener('click', this.hideLayerPopup)
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.hideLayerPopup)
    }

    closePopup = e => {
        const popup = document.getElementById('layers-popup')
        if (!popup.contains(e.target)) {
            this.setState({ activePopup: '' })
            window.removeEventListener('click', this.closePopup)
        }
    }

    toggleLayer = layer => {
        this.props.toggleLayer(layer.id)
    }

    toggleLayerPopup = layer => {
        const { activePopup } = this.state
        this.setState({ activePopup: activePopup === layer.name ? null : layer.name })
    }

    hideLayerPopup = e => {
        const { activePopup } = this.state

        if (activePopup === null || this.popupRef.current === null) {
            return
        }

        if (this.popupRef.current.contains(e.target) || this.refMap[activePopup].current.contains(e.target)) {
            return
        }

        this.setState({ activePopup: null })
    }

    renderLayerTitleSection = layer => (
        <div className="layer-title-section">
            <Checkbox
                checked={layer.visible}
                additionalClasses="is-pulled-left"
                onClick={() => this.toggleLayer(layer)}
                aria-label="Checkbox to select this layer"
            />
            <h6 className="title is-static-size-6">
                <div
                    className="layer-title"
                    onClick={() => this.toggleLayerPopup(layer)}
                    onKeyDown={e => (e.key === 'Enter' ? this.toggleLayerPopup(layer) : null)}
                    role="button"
                    tabIndex="0"
                    style={{ cursor: 'pointer' }}
                >
                    {layer.name}
                    {layer.isNew && (
                        <div className="new-layer">
                            <SVGWrapper title="This layer is new.">
                                <NewIcon width="18px" height="18px" />
                            </SVGWrapper>{' '}
                            <span aria-hidden="true">NEW</span>
                        </div>
                    )}
                </div>
            </h6>
            {layer.dbLink && (
                <div className="db-icon">
                    <a href={layer.dbLink} target="_blank" rel="noopener noreferrer" title="View in Data Basin">
                        <img src={DataBasinLogo} className="layer-icon" alt="Data Basin Logo" />
                    </a>
                </div>
            )}
            {layer.externalLink && (
                <div className="db-icon">
                    <a href={layer.externalLink} target="_blank" rel="noopener noreferrer" title="View External Link">
                        <img src={ExternalLogo} className="layer-icon" alt="External Link Icon" />
                    </a>
                </div>
            )}
            <div className="info-icon">
                <button
                    type="button"
                    onClick={() => this.toggleLayerPopup(layer)}
                    onKeyDown={e => (e.key === 'Enter' ? this.toggleLayerPopup(layer) : null)}
                    tabIndex="0"
                >
                    <span className="svg-icon svg-primary-primary svg-secondary-white">
                        <SVGWrapper title="Information icon">
                            <InfoIcon width="20px" height="20px" />
                        </SVGWrapper>
                    </span>
                </button>
            </div>
        </div>
    )

    createPopup = layer => {
        const element = this.refMap[layer.name].current
        const boundingRect = element.getBoundingClientRect()
        const [left, top] = [boundingRect.left - 5, boundingRect.top - 5]
        return ReactDOM.createPortal(
            <div className="layers-popup" ref={this.popupRef} style={{ left, top }}>
                {this.renderLayerTitleSection(layer)}
                {layer.opacity !== null && (
                    <div className="slider-with-icon">
                        <div className="opacity-icon" />
                        <Slider value={layer.opacity} onChange={opacity => this.props.setOpacity(layer.id, opacity)} />
                    </div>
                )}
                <div className="description">{layer.description}</div>
            </div>,
            document.getElementById('popupPortal')
        )
    }

    render() {
        const { basemap, bounds, contextualLayers, setBasemap, zoom, setDialog } = this.props
        const merc = new SphericalMercator({ size: 256 })
        const roundedZoom = Math.round(zoom)
        const [xMin, yMin, xMax, yMax] = Object.values(merc.xyz(bounds, roundedZoom))
        const [xAvg, yAvg] = [Math.round((xMin + xMax) / 2), Math.round((yMin + yMax) / 2)]

        return (
            <div className="layers">
                <div className="content-section grow scroll">
                    <h6 className="title is-6">Context Layers</h6>
                    <p>
                        Add or remove extra layers from the map. Click on the{' '}
                        <span className="svg-icon svg-primary-primary svg-secondary-white">
                            <SVGWrapper title="Information icon">
                                <InfoIcon width="18px" height="18px" />
                            </SVGWrapper>
                        </span>{' '}
                        icon to the right of each layer to see a description and adjust opacity. Click on the{' '}
                        <img alt="Databasin" src={DataBasinLogo} style={{ height: '18px', verticalAlign: 'middle' }} />{' '}
                        icon to download the data from DataBasin and the{' '}
                        <img
                            alt="External Link"
                            src={ExternalLogo}
                            style={{ height: '18px', verticalAlign: 'middle' }}
                        />{' '}
                        icon to download the data from the California State Geoportal .
                    </p>
                    <p>
                        <a
                            onClick={() => setDialog('error')}
                            tabIndex="0"
                            onKeyDown={e => (e.key === 'Enter' ? setDialog('error') : null)}
                        >
                            Click here to report any errors or issues.
                        </a>
                    </p>
                    <div className="layers-list">
                        <Accordion
                            content={this.categories.map(category => ({
                                id: category,
                                heading: <h6 className="title is-6 is-normal">{category}</h6>,
                                stagingOnly:
                                    contextualLayers.filter(
                                        layer => layer.category === category && layer.access === this.props.staging
                                    ).length === 0
                                        ? true
                                        : false,
                                count: contextualLayers.filter(layer => layer.category === category && layer.visible)
                                    .length,
                                body: (
                                    <div className="categorized-layers">
                                        {contextualLayers
                                            .filter(layer => layer.category === category)
                                            .map(layer => (
                                                // Leaving this in to filter out new layers going forward (add `access: staging` in config.js)
                                                <Flags
                                                    authorizedFlags={
                                                        layer.access === 'staging' ? ['staging'] : ['live']
                                                    }
                                                    key={layer.name}
                                                >
                                                    <div className="layer-section">
                                                        <div ref={this.refMap[layer.name]}>
                                                            {this.renderLayerTitleSection(layer)}
                                                        </div>
                                                        {this.state.activePopup === layer.name &&
                                                            this.createPopup(layer)}
                                                    </div>
                                                </Flags>
                                            ))}
                                    </div>
                                )
                            }))}
                        />
                    </div>
                </div>
                <div className="content-section basemaps">
                    <h6 className="title is-6">Base Layers</h6>
                    {basemaps.map(bm => (
                        <div
                            key={bm.name}
                            className="basemap-icon"
                            onClick={() => setBasemap(bm.name)}
                            onKeyDown={e => (e.key === 'Enter' ? setBasemap(bm.name) : null)}
                            role="button"
                            tabIndex="0"
                        >
                            <img
                                src={`https://api.mapbox.com/styles/v1/mapbox/${bm.style}/static/-121.4853,38.565,9,0/300x300?access_token=${process.env.MAPBOX_TOKEN}`}
                                alt={bm.name}
                                className={bm === basemap ? 'active' : ' '}
                            />
                        </div>
                    ))}
                </div>
            </div>
        )
    }
}

LayersComponent.propTypes = {
    // redux state:
    basemap: PropTypes.shape({
        name: PropTypes.string,
        url: PropTypes.string,
        attribution: PropTypes.string,
        maxZoom: PropTypes.number,
        subdomains: PropTypes.array
    }).isRequired,
    bounds: PropTypes.arrayOf(PropTypes.number),
    contextualLayers: PropTypes.arrayOf(PropTypes.object).isRequired,
    zoom: PropTypes.number,
    // redux actions:
    setBasemap: PropTypes.func,
    setOpacity: PropTypes.func,
    toggleLayer: PropTypes.func,
    setDialog: PropTypes.func.isRequired
}

LayersComponent.defaultProps = {
    bounds: [-124, 32, -114, 42],
    setBasemap: () => {},
    setOpacity: () => {},
    toggleLayer: () => {},
    zoom: 7
}

export default connect(
    ({ layers: { contextualLayers, basemap }, mapSettings: { bounds, zoom } }) => ({
        basemap,
        bounds,
        contextualLayers: contextualLayersConfig.map(ctx => {
            const layer = contextualLayers.find(lyr => ctx.id === lyr.id)
            if (layer) {
                return { ...ctx, ...layer, visible: true }
            }

            return { ...ctx, visible: false }
        }),
        zoom
    }),
    dispatch => ({
        setBasemap: basemap => dispatch(setBasemapConnect(basemap)),
        setOpacity: (layer, opacity) => dispatch(setOpacityConnect(layer, opacity)),
        toggleLayer: layer => dispatch(toggleLayerConnect(layer)),
        setDialog: modalName => dispatch(setDialogConnect(modalName))
    })
)(LayersComponent)
